<template>
  <div class="measurement-history-panel">
    <div class="panel-header">
      <h4 class="panel-title">
        <i class="fas fa-ruler-combined"></i>
        {{ $t('annotation.measurements') }}
      </h4>
      <button
        v-if="measurements.length > 0"
        class="btn-clear"
        :title="$t('annotation.clearAll')"
        @click="$emit('clear-all')"
      >
        <i class="fas fa-trash"></i>
      </button>
    </div>

    <div class="panel-content">
      <div v-if="measurements.length === 0" class="empty-state">
        <i class="fas fa-ruler-combined"></i>
        <p>{{ $t('annotation.noMeasurements') }}</p>
      </div>

      <div v-else class="measurement-list">
        <div
          v-for="(measurement, index) in measurements"
          :key="index"
          class="measurement-item"
          :class="{ selected: selectedIndex === index }"
          @click="$emit('measurement-select', index)"
        >
          <div class="measurement-icon">
            <i :class="getMeasurementIcon(measurement.type)"></i>
          </div>
          <div class="measurement-details">
            <div class="measurement-type">
              {{ getMeasurementTypeName(measurement.type) }}
            </div>
            <div class="measurement-value">
              {{ measurement.value }}
            </div>
            <div v-if="measurement.pageNumber" class="measurement-page">
              Page {{ measurement.pageNumber }}
            </div>
          </div>
          <button
            class="btn-delete"
            :title="$t('common.delete')"
            @click.stop="$emit('measurement-delete', index)"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>

    <div v-if="measurements.length > 0" class="panel-footer">
      <AppButton
        variant="outline"
        size="small"
        @click="$emit('export')"
      >
        <i class="fas fa-file-export"></i>
        {{ $t('annotation.exportMeasurements') }}
      </AppButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import AppButton from '@/components/base/AppButton.vue';

interface Measurement {
  type: 'distance' | 'area' | 'polygon';
  value: string;
  pageNumber?: number;
}

interface Props {
  measurements: Measurement[];
  selectedIndex?: number;
}

defineProps<Props>();

defineEmits<{
  (e: 'measurement-select', index: number): void;
  (e: 'measurement-delete', index: number): void;
  (e: 'clear-all'): void;
  (e: 'export'): void;
}>();

const { t } = useI18n();

const getMeasurementIcon = (type: string): string => {
  switch (type) {
    case 'distance':
      return 'fas fa-ruler';
    case 'area':
      return 'fas fa-draw-polygon';
    case 'polygon':
      return 'fas fa-shapes';
    default:
      return 'fas fa-ruler-combined';
  }
};

const getMeasurementTypeName = (type: string): string => {
  switch (type) {
    case 'distance':
      return t('annotation.distance');
    case 'area':
      return t('annotation.area');
    case 'polygon':
      return t('annotation.polygonArea');
    default:
      return t('annotation.measurement');
  }
};
</script>

<style scoped>
.measurement-history-panel {
  display: flex;
  flex-direction: column;
  width: 280px;
  height: 100%;
  background: var(--surface);
  border-left: 1px solid var(--border);
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-md);
  border-bottom: 1px solid var(--border);
  background: var(--surface-secondary);
}

.panel-title {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.btn-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.btn-clear:hover {
  background: var(--surface-hover);
  color: var(--danger);
}

.panel-content {
  flex: 1;
  overflow-y: auto;
  padding: var(--space-sm);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: var(--text-secondary);
  text-align: center;
  padding: var(--space-xl);
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: var(--space-md);
  opacity: 0.3;
}

.measurement-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
}

.measurement-item {
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
  padding: var(--space-sm);
  background: var(--surface-secondary);
  border: 1px solid var(--border-light);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.2s;
}

.measurement-item:hover {
  background: var(--surface-hover);
  border-color: var(--primary);
}

.measurement-item.selected {
  background: var(--primary-light);
  border-color: var(--primary);
}

.measurement-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: var(--primary-light);
  border-radius: var(--border-radius);
  color: var(--primary);
  flex-shrink: 0;
}

.measurement-details {
  flex: 1;
  min-width: 0;
}

.measurement-type {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
  text-transform: uppercase;
  margin-bottom: var(--space-xs);
}

.measurement-value {
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  margin-bottom: var(--space-xs);
  word-break: break-word;
}

.measurement-page {
  font-size: var(--font-size-xs);
  color: var(--text-secondary);
}

.btn-delete {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  opacity: 0;
}

.measurement-item:hover .btn-delete {
  opacity: 1;
}

.btn-delete:hover {
  background: var(--danger-light);
  color: var(--danger);
}

.panel-footer {
  padding: var(--space-md);
  border-top: 1px solid var(--border);
  background: var(--surface-secondary);
}

/* Scrollbar styling */
.panel-content::-webkit-scrollbar {
  width: 6px;
}

.panel-content::-webkit-scrollbar-track {
  background: var(--surface-secondary);
}

.panel-content::-webkit-scrollbar-thumb {
  background: var(--border);
  border-radius: 3px;
}

.panel-content::-webkit-scrollbar-thumb:hover {
  background: var(--text-secondary);
}

/* Responsive */
@media (max-width: 768px) {
  .measurement-history-panel {
    width: 100%;
    border-left: none;
    border-top: 1px solid var(--border);
  }
}
</style>
